import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Brain, Sparkle, Check, X, PencilSimple, TrashSimple } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";

const FIELD_LABEL = {
  workstream: "Workstream", owner: "Owner", deadline: "Deadline",
  mission_impact: "Mission", revenue_impact: "Revenue", urgency: "Urgency",
  relationship_impact: "Relationship", risk_reduction: "Risk-reduc", effort: "Effort",
  status: "Status", title: "Title", expected_result: "Expected result",
  next_action: "Next action", stage: "Stage", category: "Category",
  probability: "Probability", impact: "Impact", mitigation: "Mitigation",
  publish_date: "Publish date", record_date: "Record date", theme: "Theme",
  mission_alignment_score: "Mission", credibility_score: "Credibility",
  audience_score: "Audience", mission_alignment: "Mission",
  estimated_value: "Est. value", contracted_value: "Contracted",
  deliverables: "Deliverables", contact: "Contact", name: "Name",
  org: "Org", options: "Options", recommendation: "Recommendation",
  risks: "Risks", context: "Context",
};

function fmt(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ") || "—";
  if (typeof v === "string" && v.length > 60) return v.slice(0, 60) + "…";
  return String(v);
}

function DiffTable({ changed, otype }) {
  const rows = Object.entries(changed || {});
  if (!rows.length) return <div className="text-xs text-[color:var(--oz-text-muted)]">No field changes.</div>;
  return (
    <table className="w-full text-xs mt-2">
      <thead>
        <tr className="text-[color:var(--oz-text-muted)] border-b border-[color:var(--oz-border)]">
          <th className="text-left overline py-1.5 pr-3">Field</th>
          <th className="text-left overline py-1.5 pr-3">AI original</th>
          <th className="text-left overline py-1.5">Mark corrected</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} className="border-b border-[color:var(--oz-border)] last:border-0">
            <td className="py-1.5 pr-3 font-mono text-[color:var(--oz-text-dim)]">{FIELD_LABEL[k] || k}</td>
            <td className="py-1.5 pr-3">
              <span className="bg-red-500/15 text-red-300 px-1.5 py-0.5 rounded-sm line-through">{fmt(v.from)}</span>
            </td>
            <td className="py-1.5">
              <span className="bg-emerald-500/15 text-emerald-300 px-1.5 py-0.5 rounded-sm">{fmt(v.to)}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function IntakeLab() {
  const [stats, setStats] = useState(null);
  const [corrections, setCorrections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [learning, setLearning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState(false);
  const [addendumDraft, setAddendumDraft] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([api.get("/intake/stats"), api.get("/intake/corrections")]);
      setStats(s.data);
      setCorrections(c.data);
      setAddendumDraft(s.data.current_addendum || "");
    } catch { toast.error("Failed to load intake lab"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doPreview = async () => {
    setLearning(true);
    try {
      const { data } = await api.post("/intake/learn", { dry_run: true });
      setPreview(data);
      toast.success(`Analyzed ${data.corrections_analyzed} corrections`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Learn failed");
    } finally { setLearning(false); }
  };

  const doCommit = async () => {
    setLearning(true);
    try {
      await api.post("/intake/learn", { dry_run: false });
      toast.success("Classifier addendum saved. Next intake calls will use it.");
      setPreview(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setLearning(false); }
  };

  const saveAddendum = async () => {
    try {
      await api.put("/intake/addendum", { value: addendumDraft });
      toast.success("Addendum saved");
      setEditing(false);
      await load();
    } catch { toast.error("Save failed"); }
  };

  const clearAddendum = async () => {
    if (!window.confirm("Clear the current addendum? The classifier will fall back to the base prompt.")) return;
    await api.delete("/intake/addendum");
    toast.success("Addendum cleared");
    await load();
  };

  if (loading) return <div className="p-8 text-[color:var(--oz-text-dim)]">Loading intake lab…</div>;

  const readyPct = Math.min(100, Math.round((stats.total_corrections / stats.recommended_min_for_quality) * 100));

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <PageHeader
        overline="Learn intake"
        title="Intake Lab"
        subtitle="Every time you correct a field on an intake-captured object, we stash the diff. Feed the diffs to Claude and the classifier stops making the same mistakes."
      />

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="oz-card p-4">
          <div className="overline mb-1">Intakes captured</div>
          <div className="font-mono text-2xl">{stats.total_intakes}</div>
        </div>
        <div className="oz-card p-4">
          <div className="overline mb-1">Corrections</div>
          <div className="font-mono text-2xl">{stats.total_corrections}</div>
          <div className="text-[10px] text-[color:var(--oz-text-muted)] mt-1 font-mono">
            recommended: {stats.recommended_min_for_quality}
          </div>
          <div className="prio-bar mt-2">
            <div className="prio-fill" style={{ width: `${readyPct}%`, background: readyPct >= 100 ? "var(--oz-emerald)" : "var(--oz-gold)" }} />
          </div>
        </div>
        <div className="oz-card p-4">
          <div className="overline mb-1">Correction rate</div>
          <div className="font-mono text-2xl text-[color:var(--oz-gold)]">
            {Math.round((stats.correction_rate || 0) * 100)}%
          </div>
          <div className="text-[10px] text-[color:var(--oz-text-muted)] mt-1 font-mono">of intakes edited</div>
        </div>
        <div className="oz-card p-4">
          <div className="overline mb-1">Avg fields changed</div>
          <div className="font-mono text-2xl">{stats.avg_fields_changed}</div>
          <div className="text-[10px] text-[color:var(--oz-text-muted)] mt-1 font-mono">per correction</div>
        </div>
      </div>

      {/* Top corrected fields */}
      {stats.top_corrected_fields?.length > 0 && (
        <div className="oz-card p-5 mb-6">
          <div className="overline mb-3">Fields Mark corrects most</div>
          <div className="flex flex-wrap gap-2">
            {stats.top_corrected_fields.map((f) => (
              <span key={f.field} className="chip chip-gold" data-testid={`top-field-${f.field}`}>
                {FIELD_LABEL[f.field] || f.field} <span className="font-mono ml-1">×{f.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Current addendum */}
      <div className="oz-card p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Brain size={16} color="var(--oz-gold)" />
            <div className="overline">Current classifier addendum</div>
          </div>
          <div className="flex gap-2">
            {stats.current_addendum && (
              <button onClick={clearAddendum} className="btn-ghost text-xs flex items-center gap-1" data-testid="clear-addendum-btn">
                <TrashSimple size={12} /> Clear
              </button>
            )}
            {!editing && (
              <button onClick={() => setEditing(true)} className="btn-ghost text-xs flex items-center gap-1" data-testid="edit-addendum-btn">
                <PencilSimple size={12} /> Edit
              </button>
            )}
          </div>
        </div>
        {editing ? (
          <div>
            <textarea
              value={addendumDraft}
              onChange={(e) => setAddendumDraft(e.target.value)}
              rows={8}
              className="w-full bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] rounded-sm p-3 text-sm leading-relaxed font-mono"
              data-testid="addendum-textarea"
            />
            <div className="flex gap-2 mt-2">
              <button onClick={saveAddendum} className="btn-primary text-xs" data-testid="save-addendum-btn">Save</button>
              <button onClick={() => { setEditing(false); setAddendumDraft(stats.current_addendum || ""); }} className="btn-ghost text-xs">Cancel</button>
            </div>
          </div>
        ) : (
          <pre className="text-sm leading-relaxed text-[color:var(--oz-text)] whitespace-pre-wrap font-sans" data-testid="current-addendum">
            {stats.current_addendum || <span className="text-[color:var(--oz-text-muted)] italic">No addendum yet — the classifier is running on the base prompt.</span>}
          </pre>
        )}
      </div>

      {/* Learn */}
      <div className="oz-card p-5 mb-6">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex-1">
            <div className="font-display text-lg mb-1">Retrain the classifier</div>
            <div className="text-xs text-[color:var(--oz-text-dim)]">
              Send all {stats.total_corrections} corrections to Claude Sonnet 4.5. It reads how you rewrote the AI's field assignments and outputs a bullet-list addendum that gets appended to the intake system prompt — permanently.
              {stats.total_corrections < 30 && (
                <span className="text-[color:var(--oz-amber)]"> Below 30 corrections, expect directional gains — the more corrections, the sharper the calls.</span>
              )}
            </div>
          </div>
          <button
            onClick={doPreview}
            disabled={learning || !stats.ready_to_learn}
            className="btn-primary text-sm flex items-center gap-1 shrink-0"
            data-testid="learn-intake-btn"
          >
            <Sparkle size={12} weight="fill" /> {learning ? "Analyzing…" : "Preview new addendum"}
          </button>
        </div>
        {!stats.ready_to_learn && (
          <div className="text-xs text-[color:var(--oz-amber)] font-mono">Need at least 3 corrections to run analysis.</div>
        )}

        {preview && (
          <div className="mt-5 pt-5 border-t border-[color:var(--oz-border)] animate-fade-in" data-testid="preview-panel">
            <div className="overline mb-2" style={{ color: "var(--oz-emerald)" }}>Proposed new addendum</div>
            <pre className="text-sm leading-relaxed p-3 border border-[color:var(--oz-emerald)]/40 bg-[color:var(--oz-emerald)]/5 rounded-sm mb-3 whitespace-pre-wrap font-sans">
              {preview.new_addendum}
            </pre>
            <div className="flex gap-2">
              <button onClick={doCommit} disabled={learning} className="btn-primary text-sm flex items-center gap-1" data-testid="commit-intake-learn-btn">
                <Check size={12} /> Adopt this addendum
              </button>
              <button onClick={() => setPreview(null)} className="btn-ghost text-sm flex items-center gap-1">
                <X size={12} /> Discard
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Corrections timeline */}
      <div>
        <div className="overline mb-3">Recent corrections ({corrections.length})</div>
        {corrections.length === 0 && (
          <div className="oz-card p-8 text-center text-sm text-[color:var(--oz-text-muted)]">
            No corrections captured yet. Use the intake box on the Command dashboard, then edit any field on the resulting object — the diff will show up here.
          </div>
        )}
        <div className="space-y-3">
          {corrections.map((c) => (
            <div key={c.id} className="oz-card p-4" data-testid={`correction-${c.object_id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <span className="chip chip-gold mr-2">{c.otype.toUpperCase()}</span>
                  {c.source_text && (
                    <span className="text-[11px] text-[color:var(--oz-text-muted)] font-mono">
                      "{c.source_text.slice(0, 140)}"
                    </span>
                  )}
                </div>
                <div className="text-[10px] font-mono text-[color:var(--oz-text-muted)] shrink-0">
                  {new Date(c.updated_at || c.created_at).toLocaleString()}
                </div>
              </div>
              <DiffTable changed={c.changed_fields} otype={c.otype} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
